import { Link } from 'react-router-dom';
import {
  Linkedin,
  Heart
} from 'lucide-react';
import { useLanguage } from '../i18n';

const socialLinks = [
  { name: 'LinkedIn', icon: Linkedin, href: 'https://www.linkedin.com/company/medkit-ai/' },
];

export const Footer = () => {
  const { t, dir } = useLanguage();

  const footerLinks = {
    product: [
      { name: t.nav.solutions, path: '/solutions' },
      { name: t.nav.pricing, path: '/pricing' },
    ],
    company: [
      { name: t.nav.contact, path: '/contact' },
    ],
    legal: [
      { name: t.footer.privacy, path: '/privacy' },
      { name: t.footer.terms, path: '/terms' },
      { name: t.footer.hipaaGdpr, path: '/compliance' },
      { name: t.footer.cookies, path: '/cookies' },
    ],
  };

  return (
    <footer className="bg-bark-700 text-sand-300" dir={dir}>
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-forest to-forest-400 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>
              <span className="text-xl font-bold text-white">
                Med<span className="text-forest-400">Kit</span>.ai
              </span>
            </Link>
            <p className="text-sand-400 mb-6 max-w-sm">
              {t.footer.description}
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 rounded-lg bg-bark-600 flex items-center justify-center hover:bg-forest hover:text-white transition-all"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t.footer.product}</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sand-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t.footer.company}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sand-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t.footer.legal}</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sand-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-bark-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sand-500 text-sm">
              © {new Date().getFullYear()} MedKit.ai. {t.footer.rights}
            </p>
            <div className="flex items-center gap-6 text-sm text-sand-500">
              <span className="flex items-center gap-1">
                {t.footer.madeWith} <Heart className="w-4 h-4 text-red-500 fill-red-500" /> {t.footer.forHealthcare}
              </span>
              <span>HIPAA & GDPR</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
